import { ChangeEvent, useEffect, useRef, useState } from "react";
import Text from "./Text";
import ImageInput from "./ImageInput";
import { useLibrary } from "@/providers/LibraryProvider";
import storageApi from "@/api/storage";
import { ClipArt } from "@/interfaces/clip-art.interface";
import ImageSelector from "./ImageSelector";
import { v4 } from "uuid";
import { Upload, useUpload } from "@/providers/UploadProvider";
import { fileFilters, fileUploadsPreprocess } from "@/utils/common";
import { uploadManyClipArts } from "@/utils/clip-art";
import { Button } from "primereact/button";
import { X } from "lucide-react";
import { useToastStore } from "@/stores/toast.store";

interface Props {
  className?: string;
  label: string;
  defaultValue?: string;
  onChange?: (clipArt?: ClipArt) => void;
}

export default function ClipArtInputField({
  className,
  label,
  defaultValue,
  onChange,
}: Props) {
  const [src, setSrc] = useState(defaultValue || "");
  const selectClipArt = useLibrary().selectClipArt;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const startUpload = useUpload().startUpload;
  const addToast = useToastStore((state) => state.addToast);

  useEffect(() => {
    setSrc(defaultValue || "");
  }, [defaultValue]);

  const onClick = async () => {
    const clipArt = await selectClipArt();
    if (!clipArt) return;
    setSrc(storageApi.getURLFromPath(clipArt.path));
    onChange?.(clipArt);
  };

  const onRemove = () => {
    setSrc("");
    onChange?.(undefined);
  };

  const onChooseFile = () => {
    fileInputRef.current?.click();
  };

  const onUploadClipArts = async (
    files: File[],
    onProgress: (data: string) => void,
  ) => {
    setUploading(true);
    await uploadManyClipArts(files, undefined, onProgress)
      .then((items) => {
        const clipArt = items[0];
        if (!clipArt) return;
        onChange?.({
          _id: "",
          categoryId: "",
          ancestorIds: [],
          path: clipArt.s3Path,
          optionId: 0,
          name: clipArt.name,
        });
        setSrc(storageApi.getURLFromPath(clipArt.s3Path));
      })
      .catch(() => {})
      .finally(() => setUploading(false));
  };

  const onUploadFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList) return;

    const files: File[] = Array.from(fileList);
    event.target.value = "";

    const accept = "image";
    const { validFiles, invalidFiles } = await fileUploadsPreprocess(
      files,
      accept,
    );

    if (invalidFiles.length > 0) {
      addToast({
        severity: "error",
        summary: "Error",
        detail: `Unsupported ${accept} file${invalidFiles.length > 1 ? "s" : ""}: ${invalidFiles.map((f) => f.name).join(", ")}`,
      });
      return;
    }

    const total = validFiles.length;

    const totalFilesString = `${total} file${total > 1 ? "s" : ""}`;

    const upload: Upload = {
      id: v4(),
      name: `${totalFilesString}`,
      type: "file",
      status: "pending",
      data: validFiles,
      handler: onUploadClipArts,
    };
    startUpload(upload);
  };

  const visible = uploading || !src;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Text text={label} size="medium" weight="medium" />
      <div className="group relative flex w-full flex-1 overflow-hidden">
        <div
          className={`${src ? "opacity-100" : "opacity-0"} flex w-full flex-1`}
        >
          <ImageInput src={src} />
        </div>
        <div
          className={`${visible ? "block" : "hidden"} absolute inset-0 rounded bg-[#e5e7eb70] group-hover:block`}
        >
          <ImageSelector
            uploading={uploading}
            onSelect={onClick}
            onUpload={onChooseFile}
          />
        </div>
        {src && (
          <div className={`absolute top-0.5 right-1.5 group-hover:block`}>
            <Button
              severity="danger"
              type="button"
              style={{ padding: 0, width: 20, height: 20 }}
              icon={<X size={14} />}
              onClick={onRemove}
            />
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={onUploadFile}
      />
    </div>
  );
}
